/**
 * Sprint 6B-15, 6B-16, 6B-17: Handoff Notification & Responsibility System
 * 
 * This module provides:
 * - 6B-15: DEPOSIT_PAID handoff notifications
 * - 6B-16: Visual responsibility badge/indicator logic
 * - 6B-17: Handoff confirmation modal triggers
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// =============================================================================
// 6B-15: HANDOFF NOTIFICATIONS
// =============================================================================

/**
 * Define which transitions trigger handoff notifications
 * Key: "fromStatus->toStatus", Value: notification config
 */
export const HANDOFF_NOTIFICATIONS = {
  'OFFER_SENT->DEPOSIT_PAID': {
    type: 'handoff',
    severity: 'high',
    title: 'New Booking — Deposit Paid',
    message: 'Patient has paid deposit. Clinical team should prepare for arrival.',
    targetRoles: ['provider_admin', 'provider_doctor'],
    icon: 'handoff'
  },
  'DEPOSIT_PAID->IN_TREATMENT': {
    type: 'status',
    severity: 'info',
    title: 'Patient Treatment Started',
    message: 'Patient has arrived and treatment has begun.',
    targetRoles: ['provider_admin', 'provider_sales_rep'],
    icon: 'treatment'
  },
  'IN_TREATMENT->POST_OP': {
    type: 'status',
    severity: 'success',
    title: 'Treatment Complete',
    message: 'Treatment completed successfully. Patient entering recovery.',
    targetRoles: ['provider_admin', 'provider_sales_rep'],
    icon: 'success'
  },
  'POST_OP->CLOSED_WON': {
    type: 'celebration',
    severity: 'success',
    title: 'Journey Complete! 🎉',
    message: 'Patient journey successfully completed.',
    targetRoles: ['provider_admin', 'provider_sales_rep', 'provider_doctor'],
    icon: 'celebration'
  }
};

/**
 * Check if a transition should trigger a handoff notification
 */
export const shouldNotifyHandoff = (fromStatus, toStatus) => {
  const key = `${fromStatus}->${toStatus}`;
  return HANDOFF_NOTIFICATIONS[key] || null;
};

// =============================================================================
// 6B-16: RESPONSIBILITY BADGE CONFIGURATION
// =============================================================================

/**
 * Responsibility configuration for each status
 */
export const RESPONSIBILITY_CONFIG = {
  NEW: {
    owner: 'provider_admin',
    ownerLabel: 'Admin',
    phase: 'Assignment',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    description: 'Awaiting assignment to a sales representative'
  },
  ASSIGNED: {
    owner: 'provider_sales_rep',
    ownerLabel: 'Sales',
    phase: 'Pre-Sales',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    description: 'Sales representative making first contact'
  },
  CONTACTED: {
    owner: 'provider_sales_rep',
    ownerLabel: 'Sales',
    phase: 'Pre-Sales',
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    description: 'In communication, preparing offer'
  },
  OFFER_SENT: {
    owner: 'provider_sales_rep',
    ownerLabel: 'Sales',
    phase: 'Negotiation',
    color: 'bg-purple-100 text-purple-700 border-purple-300',
    description: 'Awaiting patient decision on offer'
  },
  DEPOSIT_PAID: {
    owner: 'provider_admin',
    ownerLabel: 'Clinic',
    phase: 'Handoff',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    description: 'Ready for clinical team - coordinate arrival',
    isHandoff: true
  },
  IN_TREATMENT: {
    owner: 'provider_doctor',
    ownerLabel: 'Clinical',
    phase: 'Clinical',
    color: 'bg-green-100 text-green-700 border-green-300',
    description: 'Patient receiving treatment'
  },
  POST_OP: {
    owner: 'provider_doctor',
    ownerLabel: 'Clinical',
    phase: 'Clinical',
    color: 'bg-green-100 text-green-700 border-green-300',
    description: 'Post-operative care and recovery'
  },
  CLOSED_WON: {
    owner: null,
    ownerLabel: 'Complete',
    phase: 'Closed',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    description: 'Successfully completed'
  },
  CLOSED_LOST: {
    owner: null,
    ownerLabel: 'Lost',
    phase: 'Closed',
    color: 'bg-red-100 text-red-700 border-red-300',
    description: 'Did not proceed'
  },
  CLOSED_CANCELLED: {
    owner: null,
    ownerLabel: 'Cancelled',
    phase: 'Closed',
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    description: 'Cancelled'
  }
};

/**
 * Get responsibility info for a lead status
 */
export const getResponsibilityInfo = (status) => {
  return RESPONSIBILITY_CONFIG[status] || RESPONSIBILITY_CONFIG.NEW;
};

/**
 * Get the assigned person name based on lead data
 */
export const getAssignedPersonName = (lead, salesReps = [], doctors = []) => {
  const config = RESPONSIBILITY_CONFIG[lead.status];
  if (!config || !config.owner) return null;
  
  switch (config.owner) {
    case 'provider_sales_rep':
      const rep = salesReps.find(r => r.id === lead.assignedRepId);
      return rep?.name || 'Unassigned';
    case 'provider_doctor':
      const doc = doctors.find(d => d.id === lead.assignedDoctorId);
      return doc?.name || 'Clinical Team';
    case 'provider_admin':
      return 'Admin Team';
    default:
      return null;
  }
};

// =============================================================================
// 6B-17: HANDOFF CONFIRMATION MODAL CONFIGURATION
// =============================================================================

/**
 * Define which transitions require confirmation modals
 */
export const CONFIRMATION_REQUIRED_TRANSITIONS = {
  'OFFER_SENT->DEPOSIT_PAID': {
    title: 'Confirm Deposit Payment',
    message: 'This will mark the deposit as paid and notify the clinical team to prepare for the patient\'s arrival.',
    confirmLabel: 'Confirm Payment Received',
    cancelLabel: 'Cancel',
    severity: 'warning',
    requiresNote: false,
    checkboxLabel: 'I confirm the deposit has been received and verified'
  },
  'DEPOSIT_PAID->IN_TREATMENT': {
    title: 'Start Treatment',
    message: 'This confirms the patient has arrived and treatment is beginning. The sales team will be notified.',
    confirmLabel: 'Start Treatment',
    cancelLabel: 'Cancel',
    severity: 'info',
    requiresNote: false,
    checkboxLabel: null
  },
  'POST_OP->CLOSED_WON': {
    title: 'Complete Patient Journey',
    message: 'This will mark the patient journey as successfully completed. This action cannot be undone.',
    confirmLabel: 'Mark as Complete',
    cancelLabel: 'Cancel',
    severity: 'success',
    requiresNote: false,
    checkboxLabel: 'I confirm all post-operative care is complete'
  },
  'NEW->CLOSED_LOST': {
    title: 'Close Lead as Lost',
    message: 'This will permanently close this lead. Please provide a reason.',
    confirmLabel: 'Close Lead',
    cancelLabel: 'Cancel',
    severity: 'danger',
    requiresNote: true,
    noteLabel: 'Reason for closing'
  },
  'OFFER_SENT->CLOSED_LOST': {
    title: 'Close Lead as Lost',
    message: 'The patient will not proceed. Please provide a reason for tracking.',
    confirmLabel: 'Close Lead',
    cancelLabel: 'Cancel',
    severity: 'danger',
    requiresNote: true,
    noteLabel: 'Reason (e.g., chose competitor, price too high, changed mind)'
  }
};

/**
 * Check if a transition requires confirmation
 */
export const requiresConfirmation = (fromStatus, toStatus) => {
  const key = `${fromStatus}->${toStatus}`;
  return CONFIRMATION_REQUIRED_TRANSITIONS[key] || null;
};

// =============================================================================
// NOTIFICATION CONTEXT
// =============================================================================

const HandoffNotificationContext = createContext(null);

export const HandoffNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  // Dismiss a notification (defined first to be used in addNotification)
  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Add a notification
  const addNotification = useCallback((notification) => {
    const id = `notif-${Date.now()}`;
    setNotifications(prev => [...prev, { ...notification, id, createdAt: new Date().toISOString() }]);
    
    // Auto-dismiss non-critical notifications after 5 seconds
    if (notification.severity !== 'high') {
      setTimeout(() => {
        dismissNotification(id);
      }, 5000);
    }
    
    return id;
  }, [dismissNotification]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Trigger handoff notification for a transition
  const triggerHandoffNotification = useCallback((fromStatus, toStatus, leadData) => {
    const notifConfig = shouldNotifyHandoff(fromStatus, toStatus);
    if (notifConfig) {
      addNotification({
        ...notifConfig,
        leadId: leadData.id,
        patientName: leadData.patientName,
        treatment: leadData.treatment
      });
    }
  }, [addNotification]);

  // Request confirmation for a transition
  const requestConfirmation = useCallback((fromStatus, toStatus, leadData, onConfirm, onCancel) => {
    const config = requiresConfirmation(fromStatus, toStatus);
    if (config) {
      setPendingConfirmation({
        ...config,
        fromStatus,
        toStatus,
        leadData,
        onConfirm: (note) => {
          setPendingConfirmation(null);
          onConfirm(note);
        },
        onCancel: () => {
          setPendingConfirmation(null);
          if (onCancel) onCancel();
        }
      });
      return true; // Confirmation required
    }
    return false; // No confirmation needed
  }, []);

  // Close confirmation modal
  const closeConfirmation = useCallback(() => {
    if (pendingConfirmation?.onCancel) {
      pendingConfirmation.onCancel();
    }
    setPendingConfirmation(null);
  }, [pendingConfirmation]);

  const value = {
    notifications,
    pendingConfirmation,
    addNotification,
    dismissNotification,
    clearAllNotifications,
    triggerHandoffNotification,
    requestConfirmation,
    closeConfirmation
  };

  return (
    <HandoffNotificationContext.Provider value={value}>
      {children}
    </HandoffNotificationContext.Provider>
  );
};

export const useHandoffNotification = () => {
  const context = useContext(HandoffNotificationContext);
  if (!context) {
    // Return a no-op version if not within provider
    return {
      notifications: [],
      pendingConfirmation: null,
      addNotification: () => {},
      dismissNotification: () => {},
      clearAllNotifications: () => {},
      triggerHandoffNotification: () => {},
      requestConfirmation: () => false,
      closeConfirmation: () => {}
    };
  }
  return context;
};

export default HandoffNotificationContext;
